// The ship's interior lighting, cold to warm (Grey, 2026-10-22: "the ability to change the ship's internal lighting from cold to
// warm. the ship's setting in captain's station settings"). One colour temperature for the whole ship, in kelvin, set from the
// Captain's chair (its SHIP LIGHTING page) by the Captain, whoever holds command, an officer of Captain's rank or the owner; the
// server keeps it (state.json "lighting") and sends it to everyone, so every browser lights the rooms the same.
// 6500 K is the ship as she was built: no change at all. Lower is warmer (amber white), higher colder (bluish white).
// What it tints, in the browser (client/main.ts roomLight): the scene's room lights (the hemispheric fill and the "sun"), and the
// emissive colour of the ship's white light fixtures (corridor strips, ceiling panels: materials named *_Light, *CeilLight,
// *Strip_Glow whose own glow is near white). Never the screens, holograms, signs, status or alert lights, or anything coloured.
// Pure, so tools/lightingunit.ts can test it.

export const LIGHT_K_MIN = 2700, LIGHT_K_MAX = 8000, LIGHT_K_DEFAULT = 6500, LIGHT_K_STEP = 100;
// the presets on the page, warm to cold as the slider runs left to right (NEUTRAL: as built); the slider goes between and beyond them
export const LIGHT_PRESETS: [string, number][] = [["WARM", 3400], ["SOFT", 4800], ["NEUTRAL", 6500], ["COOL", 7200], ["COLD", 8000]];
export const LIGHT_NUDGE = 300;   // the page's WARMER / COOLER buttons: a step each press

// a kelvin value from anywhere (a client's message, an old save): a whole number of tens, inside the range; anything else the default
export function cleanKelvin(v: unknown): number {
  const n = typeof v === "number" ? v : typeof v === "string" ? Number(v) : NaN;
  if (!Number.isFinite(n)) return LIGHT_K_DEFAULT;
  return Math.max(LIGHT_K_MIN, Math.min(LIGHT_K_MAX, Math.round(n / 10) * 10));
}

// the colour of a black body at k kelvin, 0..1 a channel (Tanner Helland's fit to the CIE data: close enough for lamps)
export function kelvinRgb(k: number): [number, number, number] {
  const t = Math.max(1000, Math.min(40000, k)) / 100;
  const r = t <= 66 ? 255 : 329.698727446 * Math.pow(t - 60, -0.1332047592);
  const g = t <= 66 ? 99.4708025861 * Math.log(t) - 161.1195681661 : 288.1221695283 * Math.pow(t - 60, -0.0755148492);
  const b = t >= 66 ? 255 : t <= 19 ? 0 : 138.5177312231 * Math.log(t - 10) - 305.0447927307;
  const c = (x: number) => Math.max(0, Math.min(255, x)) / 255;
  return [c(r), c(g), c(b)];
}

const LUM = (c: [number, number, number]) => 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
export const TINT_MAX = 1.2;   // no channel above this: a warm room is lit about as brightly as a neutral one, without the reds blowing out

// what to multiply the ship's own light colours by for k kelvin: exactly white at 6500 K (the ship as built); the colour's shift
// against 6500 K, brought back to the same brightness (luminance 1) as far as no channel passes TINT_MAX
export function lightTint(k: number): [number, number, number] {
  const kk = cleanKelvin(k); if (kk === LIGHT_K_DEFAULT) return [1, 1, 1];
  const a = kelvinRgb(kk), n = kelvinRgb(LIGHT_K_DEFAULT);
  const raw: [number, number, number] = [a[0] / n[0], a[1] / n[1], a[2] / n[2]];
  const s = Math.min(1 / LUM(raw), TINT_MAX / Math.max(raw[0], raw[1], raw[2]));
  return [raw[0] * s, raw[1] * s, raw[2] * s];
}

// a room's own light (Grey, 2026-10-24: "Give rooms like sick bay and engineering the ability to change the warmth of their lighting. Also
// people rooms should have that ability as well as changing the light color in their room lights"): any room (not a corridor, a tube, the
// bridge or outside) may have its own warmth, set by whoever is in it; the living quarters a colour too. Unset: the ship's lighting
export interface RoomTone { kelvin?: number; color?: string }
export const COLOUR_ROOMS: readonly string[] = ["Crew Quarters", "Hangar Quarters"];
export const TONE_COLOURS: [string, string][] = [["Blue", "#5a8cff"], ["Red", "#ff4a3d"], ["Green", "#4ad66b"], ["Purple", "#a46bff"], ["Amber", "#ffb03a"], ["Teal", "#33d1c4"], ["Pink", "#ff6fb0"]];
export const toneRoomOk = (room: string) => !!room && room !== "Bridge" && !/corridor|jefferies|tube|outside|hull|space|lift|airlock/i.test(room);
// a colour as a light tint: its hue at the ship's usual brightness (luminance 1, no channel past TINT_MAX), never quite black
export function colourTint(hex: string): [number, number, number] {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex); if (!m) return [1, 1, 1];
  const n = parseInt(m[1], 16), raw: [number, number, number] = [Math.max(0.04, ((n >> 16) & 255) / 255), Math.max(0.04, ((n >> 8) & 255) / 255), Math.max(0.04, (n & 255) / 255)];
  const s = Math.min(1 / LUM(raw), TINT_MAX * 1.6 / Math.max(raw[0], raw[1], raw[2]));
  return [raw[0] * s, raw[1] * s, raw[2] * s];
}
export function toneTint(t: RoomTone | undefined): [number, number, number] | null { return !t ? null : t.color ? colourTint(t.color) : t.kelvin ? lightTint(t.kelvin) : null; }

// a word for the page and the notices: "3400 K, warm"
export function lightWord(k: number): string {
  const kk = cleanKelvin(k);
  const w = kk >= 7600 ? "cold" : kk >= 6900 ? "cool" : kk >= 6100 ? "neutral" : kk >= 4200 ? "soft" : "warm";
  return `${kk} K, ${w}`;
}

// is this one of the ship's room-light fixtures (by its material's name and its own emissive colour, as built)? White lamps only:
// a coloured glow (status, alert, signage, the teal and amber trims) never is; nor a screen, a hologram or the theater's house lights
// (the theater dims those itself)
const FIXTURE_NAME = /(?:(?:^|_)Light|CeilLight|Ceiling_?Light|Strip_Glow)(?:\.\d+)?$/;
const NEVER = /Screen|Holo|Touch|Status|Alert|Sign|Label|Tag|Text|Grow|Theater|Red|Amber|Teal|Blue|Green|Warning|Hazard/i;
export function isRoomFixture(name: string, emissive: { r: number; g: number; b: number }): boolean {
  if (!FIXTURE_NAME.test(name) || NEVER.test(name)) return false;
  const mx = Math.max(emissive.r, emissive.g, emissive.b), mn = Math.min(emissive.r, emissive.g, emissive.b);
  return mx > 0.3 && mn / mx > 0.7;      // lit, and near white
}
